import { TResult, makeSuccess } from "../../sg-core/utils/Result";
import { ISgApiService } from "../../sg-core/services/ApiService2";

import ObservationMapper, { NewObservationMapper } from "../mappers/ObservationMapper";
import IObservation, { INewObservation } from "../interfaces/IObservation";
import { IEndpoint } from "../../sg-core/services/EndpointService";
import { IAppRoleDTO, ICategoryPermission, IPermissionDTO } from "../interfaces/ICategoryPermission";
import { SemanticICONS } from "semantic-ui-react/dist/commonjs/generic";
import IFormType from "../interfaces/IFormType";
import ICategory from "../interfaces/ICategory";
import { IRole } from "../interfaces/IAuditoinnitUser";
import IFormDefinition from "../interfaces/IFormDefinition";
import FormTypeMapper from "../mappers/FormTypeMapper";
import CategoryMapper from "../mappers/CategoryMapper";
import PermissionMapper from "../mappers/PermissionMapper";
import RoleMapper from "../mappers/RoleMapper";
import { IFormConfigResult } from "./contextService";
import FormDefinitionMapper from "../mappers/FormDefinitionMapper";
import { extractObservationIdentifiersDTO } from "../utils/observation.helper";
import { base64ToJson } from "@sg-core/utils/json.helpers";
import { extractFormDataIdentifiersDTO, TIdentifiersDTO } from "../utils/id.helper";
import _uniq from "lodash/uniq";

export interface IObservationService {
    createObservation(observation: INewObservation): Promise<TResult<{ observation: IObservation }>>;
    createObservations(observation: Array<INewObservation>): Promise<TResult<{ observations: Array<IObservation> }>>;
    getObservation(observationGuid: string | number): Promise<TResult<{ observation: IObservation; context: any }>>;

    getContextData(): Promise<TResult<IInitialContextData>>;
    getFormDefinition(formId: number): Promise<TResult<IFormDefinition>>;
    getFormDefinitionByReference(referenceName: string): Promise<TResult<IFormDefinition>>;

}

export interface IInitialContextData {
    formTypes: IFormType[]
    categories: ICategory[]
    categoryPermissions: ICategoryPermission[];
    appRoles: IRole[];
}


export interface IInitialContextResult {
    success: true;
    // action: "hae_perustiedot";
    lomakkeet: { id: number; guid: string; nimi: string; kategoria: number; lomakemaaritys: number; tyonkulku: number }[];
    kategoriat: { id: number; nimi: string; ikoni?: SemanticICONS }[];
    oikeustasot?: IPermissionDTO[];
    roolit?: IAppRoleDTO[];
}

export interface IObservationResult {
    success: true;
    action: "hae_havainto";
    havainto: {
        id: number;
        guid: string;
        // nimi: string; 
        otsikko: string;
        kuvaus: string;
        kuva?: string;
        kirjattu: string;
        kirjaaja: number;
        lomake: number;
        alue: string;
        tyo?: number | undefined;
        tila: string;
        lomakedata: string;
        on_julkinen: "Y" | "N"
        on_kasittelija?: "Y" | "N",
        karttasijainti?: string;
        kasittelija?: number;
        lomakedatahaku: string;
    }
};

export interface IObservationsResult {
    success: true;
    action: "lisaa_havainnot";
    havainnot: Array<IObservationResult["havainto"]>
};



export interface ICreateObservationBody {
    success: true;
    action: "tallenna_havainto";
    havainto: {
        // id: number;
        // nimi: string;
        otsikko: string;
        kuvaus: string;
        kuva?: string;
        kirjattu: string;
        lomakedatahaku: string;
        //  kirjaaja: number;
        lomake: number; alue: number; tyo?: number; tila: string; lomakedata: string;
        on_julkinen: "Y" | "N";
        karttasijainti?: string
    }
};

export const getObservationService = (apiService: ISgApiService, Enpoint: IEndpoint): IObservationService => {

    const createObservation: IObservationService["createObservation"] = async (observation) => {
        const action = "lisaa_havainto"
        if (!observation.formSchema) {
            throw Error("Missing form configurations");
        };

        const body = {
            havainto: NewObservationMapper.domainToApi(observation),
            tunnisteet: extractObservationIdentifiersDTO(observation.formData, observation.formSchema)
        }
        const apiResponse = await apiService.sendAction<IObservationResult>(Enpoint, action, body);

        if (apiResponse.isSuccess === true) {
            return makeSuccess({ observation: ObservationMapper.apiToDomain(apiResponse.value.havainto) });
        }
        return apiResponse;
    };



    const createObservations: IObservationService["createObservations"] = async (observations) => {
        const action = "lisaa_havainnot"

        const tunnisteet = observations.reduce<TIdentifiersDTO>((acc, observation) => {
            if (!observation.formSchema) return acc;
            const ids = extractFormDataIdentifiersDTO(observation.formData, observation.formSchema);

            if (typeof ids !== "string") {

                acc = {
                    henkilot: _uniq([...acc.henkilot.split(","), ...(ids.henkilot.split(","))]).filter((item) => item !== "").join(","),
                    kohteet: _uniq([...acc.kohteet.split(","), ...(ids.kohteet.split(","))]).filter((item) => item !== "").join(","),
                };
            }
            return acc
        }, { henkilot: "", kohteet: "" })

        // extractFormDataIdentifiersDTO(observation.formData, observation.formSchema)

        const body = {
            havainnot: observations.map(NewObservationMapper.domainToApi),
            tunnisteet
        }
        const apiResponse = await apiService.sendAction<IObservationsResult>(Enpoint, action, body);

        if (apiResponse.isSuccess === true) {
            return makeSuccess({
                observations: (apiResponse.value.havainnot || []).map((dto, index) => ({
                    ...ObservationMapper.apiToDomain(dto),
                    __noteId: (observations[index] || {}).__noteId
                }))
            });
        }
        return apiResponse;
    };





    const getContextData: IObservationService["getContextData"] = async () => {
        const action = "hae_perustiedot"
        const apiResponse = await apiService.sendAction<IInitialContextResult>(Enpoint, action, {});
        if (apiResponse.isSuccess === true) {
            return makeSuccess<IInitialContextData>({
                formTypes: apiResponse.value.lomakkeet.map(FormTypeMapper.apiToDomain),
                categories: apiResponse.value.kategoriat.map(CategoryMapper.apiToDomain),


                categoryPermissions: PermissionMapper.permissionsToDomain(apiResponse.value.oikeustasot || []),
                appRoles: apiResponse.value.roolit ? apiResponse.value.roolit.map(RoleMapper.apiToDomain) : []
            });
        }
        return apiResponse
    };


    const getFormDefinition: IObservationService["getFormDefinition"] = async (formId) => {
        const action = "hae_lomakemaaritys"
        const body = { lomake: { id: formId } }
        const apiResponse = await apiService.sendAction<IFormConfigResult>(Enpoint, action, body);

        if (apiResponse.isSuccess === true) {
            return makeSuccess(FormDefinitionMapper.apiToDomain(apiResponse.value.lomake, base64ToJson));
        }
        return apiResponse
    };



    const getFormDefinitionByReference: IObservationService["getFormDefinitionByReference"] = async (referenceName) => {
        const action = "hae_lomakemaaritys_viitenimella"
        const body = { lomake: { viitenimi: referenceName } }
        const apiResponse = await apiService.sendAction<IFormConfigResult>(Enpoint, action, body);

        if (apiResponse.isSuccess === true) {
            return makeSuccess(FormDefinitionMapper.apiToDomain(apiResponse.value.lomake, base64ToJson));
        }
        return apiResponse
    };




    const getObservation = async (observationGuid?: string | number): Promise<TResult<{ observation: IObservation; context: any }>> => {
        const action = "hae_havainto";
        const body = {
            havainto: {
                guid: typeof observationGuid === "string" ? observationGuid : undefined,
                id: typeof observationGuid === "number" ? observationGuid : undefined
            }
        };

        const apiResponse = await apiService.sendAction<IObservationResult>(Enpoint, action, body);

        if (apiResponse.isSuccess === true) {
            return makeSuccess({
                observation: ObservationMapper.apiToDomain(apiResponse.value.havainto),
                context: {
                    //@ts-ignore
                    lomakeId: apiResponse.value.lomakkeet ? apiResponse.value.lomakkeet[0].id : null
                }
            });
        }
        return apiResponse;
    };


    return {
        createObservations,
        createObservation,
        getContextData,
        getFormDefinition,
        getFormDefinitionByReference,
        getObservation
    };
};
